import type { graphTypeEnum } from '@/features/analysis/_models/project';
import { BigCalculator } from '@/utils';
import type { SortOrder } from 'antd/es/table/interface';
import type { z } from 'zod';

type GraphType = z.infer<typeof graphTypeEnum>;

/**
 * 円グラフ・ツリーマップの初期表示年度（前年度 n-1 を優先）を決める (RS-4473)。
 * 最新年度(当年度 n)の1つ前がデータに存在すればそれを、無ければ最新年度を返す。
 * `get-ks-visualizer-options.ts`(Highcharts 依存) を pure util / テストへ取り込まないよう本ファイルに置く。
 */
export const getKsDefaultGraphYear = (fiscalYears: number[]): number => {
  // 空配列だと Math.max(...[]) が -Infinity を返すため、防御的に 0 を返す。
  // 呼び出し側（分析結果の描画）はデータ存在時のみ呼ぶため実際には到達しない。
  if (fiscalYears.length === 0) {
    return 0;
  }
  const latestYear = Math.max(...fiscalYears);
  return fiscalYears.includes(latestYear - 1) ? latestYear - 1 : latestYear;
};

/**
 * 円グラフ・ツリーマップの「選択中の表示年度」を解決する。
 * `chartYear`（年度タブで選択された年度、URL state）が現在の `fiscalYears` に含まれていればそれを、
 * 無ければ既定選択年度（`getKsDefaultGraphYear`）を返す (RS-4491)。
 */
export const getKsActiveChartYear = (
  chartYear: number | null | undefined,
  fiscalYears: number[],
): number =>
  chartYear !== null &&
  chartYear !== undefined &&
  fiscalYears.includes(chartYear)
    ? chartYear
    : getKsDefaultGraphYear(fiscalYears);

type KsSortKey = {
  /** ソート基準の年度 */
  year: number;
  /** ソート方向 */
  direction: Exclude<SortOrder, null>;
};

/** `order` URL クエリの区切り文字。`buildSortUpdate()` の生成側と対で使う。 */
export const KS_ORDER_SEPARATOR = '_';

/**
 * `order` クエリ（`"<direction>_<year>"` 形式）を parse する。
 * フォーマット不正・年度が数値でない場合は null を返す。生成は `buildSortUpdate()` 側が担う。
 */
export const parseKsOrder = (
  order: string | null | undefined,
): KsSortKey | null => {
  if (!order) {
    return null;
  }
  const [direction, yearText] = order.split(KS_ORDER_SEPARATOR);
  if (!direction || !yearText) {
    return null;
  }
  const year = Number(yearText);
  if (!Number.isFinite(year)) {
    return null;
  }
  return {
    year,
    direction:
      direction === ('ascend' satisfies SortOrder) ? 'ascend' : 'descend',
  };
};

/**
 * 基金シート分析の表示順（ソートキー列）を解決する。
 * テーブルのデータソースとグラフ凡例・シリーズの並び順を同期させるため、両者でこの関数を共有する。
 *
 * - `order` が指定され、その年度が現在の `fiscalYears` に含まれていれば、その単一キーでソートする
 *   （ユーザーが列ヘッダをクリックした場合）。年度変更等で現在のデータに存在しない `order` は無視する。
 * - `order` が無い（初期表示）場合:
 *   - **organization(事業所管課室)軸は BE の並び順(GIMAコード順)をそのまま使う**ため空配列を返す。
 *   - それ以外(基金造成法人・基金事業)の軸は `graphType` により既定ソートを変える（RS-4487）:
 *     - **積み上げ棒(stackedVerticalBar)**:
 *       第1キー 前年度(n-1) 金額降順 → 第2キー 当年度(n) 金額降順 → タイブレーク 名称昇順（RS-4437 / RS-4395）。
 *     - **円グラフ(pieChart) / ツリーマップ(treeMap)**:
 *       既定選択年度（前年度 n-1 優先 = `getKsDefaultGraphYear`）の単一降順キー。
 *       年度タブで年度を選ぶと `order`(URL) が書かれ、上の `order` 分岐でその単一キーに従う。
 *
 * @param order URL クエリのソート状態（例: `"descend_2024"`）。未ソートなら null/undefined。
 * @param fiscalYears 取得データに含まれる年度の配列（重複を含む場合がある）。
 * @param analysisAxis 分析軸。`organization` のときは初期金額ソートを行わない。
 * @param graphType 選択中のグラフ種別。既定は積み上げ棒。円/ツリーは選択年度の単一降順にする。
 * @param chartYear 円/ツリーの年度タブで選択中の年度（URL state）。`order` が無い場合の
 *   円/ツリー既定ソート基準に使う。未指定なら既定選択年度（`getKsDefaultGraphYear`）を使う (RS-4491)。
 * @returns 適用順に並んだソートキー列。基準が決まらない場合は空配列。
 */
export const resolveKsSortKeys = (
  order: string | null | undefined,
  fiscalYears: number[],
  analysisAxis: string | null | undefined,
  graphType: GraphType = 'stackedVerticalBar',
  chartYear?: number | null,
): KsSortKey[] => {
  const parsed = parseKsOrder(order);
  if (parsed && fiscalYears.includes(parsed.year)) {
    return [parsed];
  }

  // organization 軸は BE の GIMAコード順を維持する（初期金額ソートをかけない）
  if (analysisAxis === 'organization') {
    return [];
  }

  if (fiscalYears.length === 0) {
    return [];
  }

  // 円グラフ・ツリーマップは選択中の表示年度（年度タブ未選択時は前年度 n-1 優先）の
  // 単一降順を初期ソートにする（RS-4487 / RS-4491）
  if (graphType === 'pieChart' || graphType === 'treeMap') {
    return [
      {
        year: getKsActiveChartYear(chartYear, fiscalYears),
        direction: 'descend',
      },
    ];
  }

  // 積み上げ棒の初期表示: 前年度(n-1) 降順 → 当年度(n) 降順（年度の昇順に降順キーを並べる）
  const ascendingYears = [...new Set(fiscalYears)].sort((a, b) => a - b);
  return ascendingYears.map((year) => ({
    year,
    direction: 'descend' as const,
  }));
};

/**
 * 列ヘッダの矢印(▽/△)表示用の sortOrder を導出する。
 *
 * `order`(URL) が明示指定されていればそれを最優先する。`order` が既定ソートへ
 * フォールバックする（`resolveKsSortKeys`）のとは異なり、`order` 自体をそのままフォールバックに
 * 使うと、ソート解除（order=null）が初期表示と区別できず同じ既定降順の矢印に戻ってしまい、
 * ヘッダクリックのサイクル(昇順/降順/解除)が壊れる (RS-4490)。
 *
 * `fallbackYear` は、円グラフ・ツリーマップのように「常にどこかの年度が実際のソート基準として
 * 使われている」ケースで、その実際のアクティブ年度（`getKsActiveChartYear` で解決した年度）を渡す。
 * `order` が未指定でも `fallbackYear` と一致する列には `descend` を表示し、
 * 「実際にはその年度でソートされているのに矢印が付かない」という見た目上の不整合を防ぐ (RS-4491)。
 * 積み上げ棒・organization軸のように「未ソートも正当な状態」であるケースでは渡さない
 * （渡さなければ従来どおり `order` 未指定時は常に null = 矢印非表示）。
 */
export const getKsColumnSortOrder = (
  order: string | null | undefined,
  fiscalYear: number,
  fallbackYear?: number | null,
): SortOrder => {
  const parsed = parseKsOrder(order);
  if (parsed) {
    return parsed.year === fiscalYear ? parsed.direction : null;
  }
  if (fallbackYear !== null && fallbackYear !== undefined) {
    return fallbackYear === fiscalYear ? 'descend' : null;
  }
  return null;
};

/**
 * 列ヘッダのソートサイクル（クリックで遷移する方向の順序）を返す。
 *
 * antd Table は「現在の sortOrder の、`sortDirections` 配列内での次の要素」を次状態とする
 * （末尾の次は解除）。円/ツリーのアクティブ年度列は order=null でもフォールバックで▽(降順)を
 * 表示するため（`getKsColumnSortOrder`）、既定サイクル(ascend→descend→解除)のままだと
 * 「降順の次=解除 → order=null → フォールバックで再び降順」というループになり
 * 昇順に到達できない。アクティブ年度列だけ降順始まり(descend→ascend→解除)に反転させ、
 * 降順→昇順→(解除=フォールバックの既定降順へ戻る)のサイクルを成立させる (RS-4491)。
 */
export const getKsColumnSortDirections = (
  fiscalYear: number,
  activeYear: number | null | undefined,
): Exclude<SortOrder, null>[] =>
  activeYear === fiscalYear ? ['descend', 'ascend'] : ['ascend', 'descend'];

type KsSortAccessors<T> = {
  /** 指定年度の金額を取り出す */
  getTotal: (item: T, year: number) => number | null | undefined;
  /** 名称を取り出す（最終タイブレークに使用） */
  getName: (item: T) => string;
};

/**
 * `resolveKsSortKeys` で得たキー列に従ってソートするコンパレータを生成する。
 * 未入力（金額が `null`/`undefined`）は金額として最小値扱いする（純粋な `0` の行とは区別する。RS-4525）。
 * そのため昇順では先頭、降順では末尾に来る。全キーが同値の場合は名称の昇順でタイブレークする。
 */
export const compareByKsSortKeys =
  <T>(keys: KsSortKey[], { getTotal, getName }: KsSortAccessors<T>) =>
  (a: T, b: T): number => {
    for (const { year, direction } of keys) {
      const rawA = getTotal(a, year);
      const rawB = getTotal(b, year);
      const isBlankA = rawA === null || rawA === undefined;
      const isBlankB = rawB === null || rawB === undefined;
      if (isBlankA && isBlankB) {
        continue;
      }
      if (isBlankA) {
        return direction === 'ascend' ? -1 : 1;
      }
      if (isBlankB) {
        return direction === 'ascend' ? 1 : -1;
      }
      const diff =
        direction === 'ascend'
          ? BigCalculator.subtract(rawA, rawB)
          : BigCalculator.subtract(rawB, rawA);
      if (diff !== 0) {
        return diff;
      }
    }
    return getName(a).localeCompare(getName(b), 'ja');
  };
